import { Drawer, DrawerContent } from '@/components/ui/drawer';
import {
  ProductDrawerContent,
  type ProductDrawerProduct,
  type ProductDrawerVariant,
} from '@/components/ProductDrawerContent';
import { ProductImageGallery } from '@/components/ProductImageGallery';
import { formatTwdFromCents } from '@pos/shared/money';

/** 預購頁商品列表項（與 campaign.products / displayProducts 結構一致） */
export interface PreorderDisplayProduct {
  productId: string;
  productName: string;
  productPriceTwd: number;
  productImageUrl: string | null;
  /** 多張圖片 URL（輪播用） */
  productImageUrls?: string[];
  remainingQuantity: number;
  productDescription?: string | null;
  hasVariants?: boolean;
  variants?: Array<{
    id: string;
    unitPriceTwd: number;
    combination?: Record<string, string>;
  }>;
}

export interface PreorderProductListProps {
  displayProducts: PreorderDisplayProduct[];
  openProductDrawer: (product: PreorderDisplayProduct) => void;
  productDrawerOpen: boolean;
  onProductDrawerOpenChange: (open: boolean) => void;
  selectedProduct: PreorderDisplayProduct | null;
  drawerQuantity: number;
  selectedByDimension: Record<string, string> | null;
  setSelectedByDimension: (
    prev:
      | Record<string, string>
      | null
      | ((p: Record<string, string> | null) => Record<string, string> | null)
  ) => void;
  selectedDrawerVariant: {
    id: string;
    unitPriceTwd: number;
    combination: Record<string, string>;
  } | null;
  onDrawerStepperMinus: () => void;
  onDrawerStepperPlus: () => void;
  onDrawerAddToCart: () => void;
  drawerStepperAtMin: boolean;
  drawerStepperAtMax: boolean;
  normalizeImageUrl: (url: string | null | undefined) => string | null;
}

/**
 * 商品列表與 Drawer（點擊卡片開啟，規格＋Stepper＋加入購物車）
 */
export function PreorderProductList({
  displayProducts,
  openProductDrawer,
  productDrawerOpen,
  onProductDrawerOpenChange,
  selectedProduct,
  drawerQuantity,
  selectedByDimension,
  setSelectedByDimension,
  selectedDrawerVariant,
  onDrawerStepperMinus,
  onDrawerStepperPlus,
  onDrawerAddToCart,
  drawerStepperAtMin,
  drawerStepperAtMax,
  normalizeImageUrl,
}: PreorderProductListProps) {
  return (
    <>
      <div className="px-4 py-4">
        <div className="space-y-3">
          {displayProducts.map((product, idx) => {
            const isOutOfStock = product.remainingQuantity <= 0;

            return (
              <div
                key={`${product.productId}-${idx}`}
                onClick={() => {
                  if (!isOutOfStock) openProductDrawer(product);
                }}
                className={`bg-white rounded-lg shadow-md overflow-hidden transition-all duration-200 flex select-none ${
                  isOutOfStock
                    ? 'opacity-50 grayscale cursor-not-allowed'
                    : 'active:scale-[0.98]'
                }`}
              >
                <div className="w-24 h-24 flex-shrink-0 bg-gray-100 flex items-center justify-center relative select-none overflow-hidden">
                  <ProductImageGallery
                    primaryUrl={product.productImageUrl}
                    imageUrls={product.productImageUrls}
                    normalizedUrls={[
                      normalizeImageUrl(product.productImageUrl),
                      ...(product.productImageUrls ?? []).map(u =>
                        normalizeImageUrl(u)
                      ),
                    ].filter((u): u is string => Boolean(u))}
                    alt={product.productName}
                    thumbnail
                    className="w-full h-full"
                    imageClassName="w-full h-full rounded-none select-none"
                  />
                  {isOutOfStock && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">
                        SOLD OUT
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex-1 p-3 flex flex-col justify-center">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="text-xs font-bold text-gray-900 line-clamp-1 flex-1 min-w-0">
                      {product.productName}
                    </h3>
                    <p className="text-xs font-semibold text-blue-600 flex-shrink-0">
                      {formatTwdFromCents(product.productPriceTwd)}
                    </p>
                  </div>
                  {product.productDescription && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {product.productDescription}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Drawer
        open={productDrawerOpen}
        onOpenChange={open => {
          onProductDrawerOpenChange(open);
          if (!open) setSelectedByDimension(null);
        }}
      >
        <DrawerContent noDrag>
          {selectedProduct &&
            (() => {
              const primaryNorm = normalizeImageUrl(
                selectedProduct.productImageUrl
              );
              const urlsNorm = (selectedProduct.productImageUrls ?? []).map(u =>
                normalizeImageUrl(u)
              );
              const allNorm = [primaryNorm, ...urlsNorm].filter(
                (u): u is string => Boolean(u)
              );
              const drawerProduct: ProductDrawerProduct = {
                productName: selectedProduct.productName,
                productDescription: selectedProduct.productDescription ?? null,
                imageUrl: primaryNorm ?? null,
                imageUrls: allNorm.length > 1 ? allNorm.slice(1) : undefined,
                productPriceTwd: selectedProduct.productPriceTwd,
                hasVariants: selectedProduct.hasVariants,
                variants: selectedProduct.variants,
              };
              const variantForContent: ProductDrawerVariant | null =
                selectedDrawerVariant
                  ? {
                      id: selectedDrawerVariant.id,
                      unitPriceTwd: selectedDrawerVariant.unitPriceTwd,
                      combination: selectedDrawerVariant.combination ?? {},
                    }
                  : null;
              return (
                <ProductDrawerContent
                  product={drawerProduct}
                  selectedByDimension={selectedByDimension}
                  setSelectedByDimension={setSelectedByDimension}
                  selectedVariant={variantForContent}
                  quantity={drawerQuantity}
                  onQuantityMinus={onDrawerStepperMinus}
                  onQuantityPlus={onDrawerStepperPlus}
                  onAddToCart={onDrawerAddToCart}
                  maxQuantity={selectedProduct.remainingQuantity}
                  stepperAtMin={drawerStepperAtMin}
                  stepperAtMax={drawerStepperAtMax}
                  addToCartLabel="加入購物車"
                />
              );
            })()}
        </DrawerContent>
      </Drawer>
    </>
  );
}
